// Local-dev preview loader (shared by CalendarView, FilmPage, TheaterDetail).
//
// When ?veeziPreview=anthology is active (see VEEZI_PREVIEW in ../constants),
// the venue is served entirely from a static export at /veezi-<venue>-preview.json
// — showtimes AND movie records — instead of Supabase/IST. Lets us vet the
// crawler's output (matches, double-feature splits, series, synopses) in the real
// UI with zero prod writes. Prod never sets the flag, so this is inert there.

import { VEEZI_PREVIEW } from '../constants';

export { VEEZI_PREVIEW };
export const VEEZI_PREVIEW_CINEMA = {
  anthology: '41518',
  'film-forum': '41515',
  metrograph: '65751',
  bam: '65749',
  ifc: '41517',
  quad: '65750',
  roxy: '65747',
  'nitehawk-wb': '65753',
  'nitehawk-pp': '65752',
  syndicated: '65900',
  low: '81344',
  spectacle: '81345',
  paris: '60750',
  flc: '66390',
  'alamo-lm': '67766',
  'alamo-bk': '60853',
  'alamo-si': '67768',
  'angelika-ny': '56432',
  'angelika-ve': '41535',
  'angelika-c123': '60749',
  momi: '65959',
  'film-noir': '81346',
  'new-plaza': '81347',
  'cobble-hill': '81348',
  'asia-society': '81349',
  'light-industry': '81350',
  'uptown-film': '81351',
  williamsburg: '81352',
  'kew-gardens': '81353',
  moma: '66783',
  // Chain (AMC + Regal) cleanup preview — see scripts/veezi/chain-cleanup-preview.js.
  // All kept chain rows are re-tagged to one venue (AMC Empire 25) for a slate view.
  chains: '41514',
  // Fathom crawler preview (2026-07-27) — see scripts/veezi/fathom-preview.js.
  // Same collapse: all 20 chain venues' would-be rows under the Empire 25 banner.
  fathom: '41514',
  // AMC-direct crawler preview (2026-07-30) — scripts/veezi/amc-direct-preview.js.
  // Multi-venue: the 13 AMC venues each render their own preview slate.
  'amc-direct': '41514',
  // Fandango-chains crawler preview (2026-07-30) — scripts/veezi/fandango-napi-preview.js.
  // Multi-venue over all 20 chain venues (AMC + Regal).
  fandango: '41514',
}[VEEZI_PREVIEW] || null;

// Multi-venue previews (the AMC+Regal cleanup) collapse MANY cinemas' cleaned rows
// into ONE preview venue. The OTHER cinemas in that group must render NOTHING while the
// preview is active — otherwise their live (still-dirty) DB rows bleed in beside the
// cleaned preview and the QA is meaningless. VEEZI_PREVIEW_SUPPRESS = the cinema ids to
// blank (the preview cinema itself is excluded, so it still shows the collapsed rows).
const CHAIN_CINEMA_IDS = [
  '41527', '41531', '41533', '41514', '41529', '41530', '41532', '41542', '41544',
  '45546', '45550', '45552', '65057', // AMC
  '41523', '41534', '56487', '45544', '45551', '45554', '48863', // Regal
];
export const VEEZI_PREVIEW_SUPPRESS = new Set(
  VEEZI_PREVIEW === 'chains'
    ? CHAIN_CINEMA_IDS.filter((id) => id !== VEEZI_PREVIEW_CINEMA)
    : []
);

// MULTI-VENUE preview (the Fathom crawler QA): the preview JSON carries rows with
// their REAL cinema_ids and every chain venue renders its own slate — no collapse.
// CalendarView serves each of these ids purely from the preview file.
const AMC_CINEMA_IDS = CHAIN_CINEMA_IDS.slice(0, 13); // the 13 AMC ids (list order above)
export const VEEZI_PREVIEW_MULTI = new Set(
  VEEZI_PREVIEW === 'fathom' || VEEZI_PREVIEW === 'fandango' ? CHAIN_CINEMA_IDS
    : VEEZI_PREVIEW === 'amc-direct' ? AMC_CINEMA_IDS
    : []
);

let _promise = null;
export function loadVeeziPreview() {
  if (!VEEZI_PREVIEW) return Promise.resolve(null);
  if (!_promise) {
    _promise = fetch(`/veezi-${VEEZI_PREVIEW}-preview.json`)
      .then(r => (r.ok ? r.json() : null))
      .catch(() => null)
      // Don't cache a failed/absent fetch for the whole session — one transient
      // miss (e.g. loading mid-export) would otherwise blank the venue until a
      // full reload. Next caller simply retries.
      .then(result => { if (result === null) _promise = null; return result; });
  }
  return _promise;
}

// Convenience: preview showtimes for a given movie_id (used by FilmPage).
export async function previewShowtimesForMovie(movieId) {
  const p = await loadVeeziPreview();
  if (!p) return null;
  return (p.showtimes || []).filter(st => String(st.movie_id) === String(movieId));
}

// Convenience: the preview movie record for a slug (used by FilmPage).
export async function previewMovieBySlug(slug) {
  const p = await loadVeeziPreview();
  if (!p) return null;
  return Object.values(p.movies || {}).find(m => m.slug === slug) || null;
}
